import React from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';
import { bpConfig, orgs, orgRawColors, gaugeOffset } from './data';

interface ScoreCardProps {
  mode: 'enterprise' | 'mssp';
  breakpoint: Breakpoint;
}

const ScoreCard: React.FC<ScoreCardProps> = ({ mode, breakpoint }) => {
  const cfg = bpConfig[breakpoint];

  if (mode === 'enterprise') {
    const o = orgs[0];
    const strokeColor = o.pct >= 90 ? '#00CED1' : '#F59E0B';
    const gs = cfg.gaugeSize;

    return (
      <div className="glass-card">
        <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 03 — Score</div>
        <div className="text-xl font-light text-white/90 mb-4">Score Your Cyber Posture</div>

        <div className="flex-1 flex flex-col items-center justify-center bg-white/[0.02] border border-white/[0.04] rounded-[12px] p-8 text-center">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div
              className="w-9 h-9 rounded-[7px] flex items-center justify-center text-sm font-medium text-white"
              style={{ background: orgRawColors[0] }}
            >
              {o.letter}
            </div>
            <div>
              <div className="text-lg text-white/90">{o.name}</div>
              <div className="text-[10px] text-white/40 mt-px">555 endpoints</div>
            </div>
          </div>

          <div className={`${cfg.gradeClass} font-light my-2`} style={{ color: strokeColor }}>{o.grade}</div>

          <div className="relative flex items-center justify-center mx-auto" style={{ width: gs, height: gs }}>
            <svg className="absolute top-0 left-0" style={{ width: gs, height: gs }} viewBox="0 0 48 48" transform="rotate(-90)">
              <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <circle
                cx="24" cy="24" r="20" fill="none"
                stroke={strokeColor} strokeWidth="3" strokeLinecap="round"
                strokeDasharray="125.66"
                strokeDashoffset={gaugeOffset(o.pct)}
                style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.16,1,0.3,1)' }}
              />
            </svg>
            <span className="text-xl relative z-10" style={{ color: strokeColor }}>{o.pct}%</span>
          </div>

          <div className="flex flex-col gap-[4px] w-full mx-auto mt-5" style={{ maxWidth: cfg.cisBarMax }}>
            <span className="text-[9px] text-white/30 tracking-[0.08em] uppercase">CIS Compliance</span>
            <div className="h-2 bg-white/[0.04] rounded-[3px] overflow-hidden flex">
              <div className="h-full bg-anchor-teal" style={{ width: `${o.pct}%`, transition: 'width 1.5s cubic-bezier(0.16,1,0.3,1)' }} />
              <div className="h-full bg-anchor-amber" style={{ width: `${Math.min(100 - o.pct, 6)}%` }} />
              <div className="h-full bg-anchor-red" style={{ width: `${Math.max(0, 100 - o.pct - 6)}%` }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MSSP mode
  const totalEp = orgs.reduce((s, o) => s + o.ep, 0);
  const avgPct = Math.round(orgs.reduce((s, o) => s + o.pct, 0) / orgs.length);
  const summaryColor = avgPct >= 90 ? '#00CED1' : '#F59E0B';

  return (
    <div className="glass-card">
      <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 03 — Score</div>
      <div className="text-xl font-light text-white/90 mb-4">Score Every Client</div>

      <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: 'thin' }}>
        <div className="flex justify-between items-center py-2.5 px-4 mb-1 border-b border-white/[0.04]">
          <span className="text-[11px] text-white/40">{orgs.length} clients · {totalEp.toLocaleString()} endpoints</span>
          <span className="text-[12px]" style={{ color: summaryColor }}>Avg {avgPct}%</span>
        </div>

        {orgs.map((o, i) => {
          const gradeColor = o.pct >= 90 ? '#00CED1' : o.pct >= 80 ? '#F59E0B' : '#EF4444';
          return (
            <div
              key={o.name}
              className="grid gap-3 items-center py-[7px] px-3.5 bg-white/[0.015] border border-white/[0.025] rounded-[6px] hover:bg-white/[0.04] transition-colors"
              style={{ gridTemplateColumns: '24px 1fr 36px 42px auto' }}
            >
              <div
                className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-medium text-white"
                style={{ background: orgRawColors[i % orgRawColors.length] }}
              >
                {o.letter}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] text-white/80 whitespace-nowrap overflow-hidden text-ellipsis">{o.name}</div>
                <div className="text-[9px] text-white/[0.35] mt-px">{o.ep} endpoints</div>
              </div>
              <div className="text-[13px] text-center" style={{ color: gradeColor }}>{o.grade}</div>
              <div className="text-[12px] text-right" style={{ color: gradeColor }}>{o.pct}%</div>
              <span
                className={`text-[9px] py-0.5 px-2 rounded-[4px] whitespace-nowrap text-center min-w-[64px] ${
                  o.alert === 'critical'
                    ? 'text-anchor-red bg-red-500/10 border border-red-500/[0.12]'
                    : 'text-anchor-teal bg-teal-500/[0.06] border border-teal-500/10'
                }`}
              >
                {o.alertText}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreCard;
